import { createContext, ReactNode, useContext, useMemo } from "react";

// Contexto para la visibilidad de columnas de las tablas agrupadas (Validados de
// Selección Masiva y Seleccionados de Selección Profesional). Esas tablas dibujan
// el encabezado y las celdas en componentes anidados (ItemProceso / TablaCandidato),
// así que en lugar de pasar la lista de columnas por props a través de toda la
// jerarquía, el componente raíz la provee por contexto y las hojas consultan
// `isColumnVisible(key)`.
//
// El valor por defecto deja TODO visible, de modo que un componente que aún no
// esté envuelto por el provider siga comportándose como antes (sin ocultar nada).

interface ColumnVisibilityContextValue {
  isColumnVisible: (key: string) => boolean;
}

const ColumnVisibilityContext = createContext<ColumnVisibilityContextValue>({
  isColumnVisible: () => true,
});

interface ColumnVisibilityProviderProps {
  visibleColumns: string[];
  children: ReactNode;
}

export const ColumnVisibilityProvider = ({
  visibleColumns,
  children,
}: ColumnVisibilityProviderProps) => {
  const value = useMemo<ColumnVisibilityContextValue>(() => {
    const visibleSet = new Set(visibleColumns);
    return { isColumnVisible: (key: string) => visibleSet.has(key) };
  }, [visibleColumns]);

  return (
    <ColumnVisibilityContext.Provider value={value}>
      {children}
    </ColumnVisibilityContext.Provider>
  );
};

export const useColumnVisibility = (): ColumnVisibilityContextValue =>
  useContext(ColumnVisibilityContext);

export default ColumnVisibilityContext;
